import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { catchError, finalize, map } from 'rxjs/operators';
import { AutoCompleteSelectionItem, IAutoCompleteService } from '../../../core/component/autocomplete-remote/autocomplete-remote.component';
import { AutoCompleteSelectionState } from '../../../core/component/autocomplete-remote/autocomplete-service.interface';
import { SelectionItem } from '../../../core/component/select/select.component';
import { MessageService } from '../../../core/service/message.service';
import { ProgressBarService } from '../../../core/service/progress-bar.service';
import { Company } from '../../model/company';
import { CostBearerCompany } from '../../model/cost-bearer-company';
import { CostPlace } from '../../model/cost-place';
import { APP_CONFIG, IAppConfig } from './../../../app.config';
import { DataService } from './../../../core/service/base/data.service';
import { Property, PropertyPostTarget } from './../../model/property';
import { IPostTargetLoader } from '../base/post-target-loader.interface';
import { PropertyTax } from '../../model/property-tax';
import { LocalDate } from '../../../core/model/local-date';

@Injectable()
export class PropertyService extends DataService<Property> implements IAutoCompleteService, IPostTargetLoader<PropertyPostTarget> {
  private readonly _defaultFilterFields: string[] = ['company', 'propertyName', 'municipality'];

  constructor(
    httpClient: HttpClient,
    @Inject(APP_CONFIG) config: IAppConfig,
    messageService: MessageService,
    progressBar: ProgressBarService
  ) {
    super(httpClient, config, messageService, Property, progressBar, 'property');
  }

  getCostPlaces(): Observable<string[]> {
    return this.client.get<string[]>(`${this.endpoint}/cost-place`);
  }

  getCostPlacesAndCompanies(): Observable<CostPlace[]> {
    return this.client.get<CostPlace[]>(`${this.endpoint}/cost-places`);
  }

  getCostBearers(): Observable<string[]> {
    return this.client.get<string[]>(`${this.endpoint}/cost-bearer`);
  }

  getCostBearersAndCompanies(): Observable<CostBearerCompany[]> {
    return this.client.get<CostBearerCompany[]>(`${this.endpoint}/cost-bearer-company`);
  }

  getFaultNotificationRecipients(): Observable<SelectionItem[]> {
    return this.client.get<SelectionItem[]>(`${this.endpoint}/fault-notification-recipients`);
  }

  getCompanyBankAccounts(id: string): Observable<SelectionItem[]> {
    return this.client.get<SelectionItem[]>(`${this.endpoint}/company-bank-accounts/${id}`);
  }

  getAppendixTitels(): Observable<SelectionItem[]> {
    return this.client.get<SelectionItem[]>(`${this.endpoint}/appendix-titles`);
  }

  getBankgiro(id: string): Observable<string> {
    return this.client.get<string>(`${this.endpoint}/payment-bankgiro/${id}`);
  }

  getFilterDefinition(): Observable<{
    company: string;
    companies: Company[];
    property: string;
    properties: Property[];
    propertyName: string;
    municipality: string;
    propertyCustomPropertiesTextValues: { key: string; value: string }[];
    propertyCustomPropertiesDateValues: { key: string; value: LocalDate }[];
    propertyCustomPropertiesNumericValues: { key: string; value: string }[];
    propertyCustomPropertiesBooleanValues: { key: string; value: boolean }[];
    selectedFields: string[];
    defaultFields: string[];
  }> {
    return this.client.get<any>(`${this.endpoint}/filter`).pipe(
      map(res => {
        return {
          company: res.company ? res.company : '',
          companies: Array.isArray(res.companies) ? res.companies.map(x => Object.assign(new Company(), x)) : [],
          property: res.property ? res.property : '',
          properties: Array.isArray(res.properties) ? res.properties.map(x => Object.assign(new Property(), x)) : [],
          propertyName: res.propertyName ? res.propertyName : '',
          municipality: res.municipality ? res.municipality : '',
          propertyCustomPropertiesTextValues: Array.isArray(res.propertyCustomPropertiesTextValues)
            ? res.propertyCustomPropertiesTextValues.map(value => ({ key: value.key, value: value.value }))
            : [],
          propertyCustomPropertiesDateValues: Array.isArray(res.propertyCustomPropertiesDateValues)
            ? res.propertyCustomPropertiesDateValues.map(value => ({ key: value.key, value: value.value }))
            : [],
          propertyCustomPropertiesNumericValues: Array.isArray(res.propertyCustomPropertiesNumericValues)
            ? res.propertyCustomPropertiesNumericValues.map(value => ({ key: value.key, value: value.value }))
            : [],
          propertyCustomPropertiesBooleanValues: Array.isArray(res.propertyCustomPropertiesBooleanValues)
            ? res.propertyCustomPropertiesBooleanValues.map(value => ({ key: value.key, value: value.value }))
            : [],
          selectedFields: Array.isArray(res.selectedFields) ? res.selectedFields : this._defaultFilterFields,
          defaultFields: this._defaultFilterFields,
        };
      }),
      catchError(error => {
        console.log(error);
        return [];
      })
    );
  }

  getPropertyOverview(filter: any): Observable<any[]> {
    this.progressBar.start();
    return this.client
      .post<any[]>(`${this.endpoint}/property-overview`, JSON.stringify(filter), { headers: { 'Content-Type': 'application/json' } })
      .pipe(
        catchError(error => {
          this.notification.showError(error);
          return [];
        }),
        finalize(() => this.progressBar.stop())
      );
  }

  getSelectionItem(id: string, state?: AutoCompleteSelectionState): Observable<AutoCompleteSelectionItem | null> {
    return super.getSelectionItemWithOptions(id, state, item => new AutoCompleteSelectionItem(item.name, item.id, item, item.shortName));
  }

  getSelection(filter: string, options?: string, page?: number): Observable<AutoCompleteSelectionItem[]> {
    return super.getSelectionWithOptions<any, string>(
      filter,
      options,
      item => new AutoCompleteSelectionItem(item.name, item.id, item, item.shortName),
      page
    );
  }

  export(ids: string[]): Observable<File> {
    return super.exportDataItems(ids, 'Fastighet.xlsx');
  }

  getPostTargetModels(companyId: string): Observable<PropertyPostTarget[]> {
    this.progressBar.start();
    return this.client.get<PropertyPostTarget[]>(`${this.endpoint}/post-target/${companyId}`).pipe(
      map(response => response.map(x => new PropertyPostTarget(x))),
      finalize(() => this.progressBar.stop())
    );
  }

  getHomeQCompanyName(id: string): Observable<string> {
    return this.client.get<string>(`${this.endpoint}/homeq-office/${id}`);
  }

  moveToCompany(propertyId: string, destinationCompanyId: string): Observable<any> {
    this.progressBar.start();
    return this.client.post(this.endpoint + '/change-company', { propertyId, destinationCompanyId }).pipe(
      finalize(() => {
        this.progressBar.stop();
      })
    );
  }
}
